<template>
  <div class="interventions-manager">
    <!-- Header Ultra-Professionnel -->
    <div class="interventions-header">
      <div class="header-overlay"></div>
      <div class="header-content">
        <div class="header-info">
          <h2 class="header-title">
            <span class="title-icon">🔧</span>
            <span class="title-text">Operations</span>
            <div class="title-underline"></div>
          </h2>
          <p class="header-subtitle">Gestion avancée des interventions techniques</p>
        </div>
        
        <div class="header-stats">
          <div class="stat-card planifiees">
            <div class="stat-icon">📅</div>
            <div class="stat-content">
              <div class="stat-value">{{ stats.planifiees }}</div>
              <div class="stat-label">Planifiées</div>
            </div>
          </div>
          
          <div class="stat-card en-cours">
            <div class="stat-icon">⚡</div>
            <div class="stat-content">
              <div class="stat-value">{{ stats.enCours }}</div>
              <div class="stat-label">En Cours</div>
            </div>
          </div>
          
          <div class="stat-card terminees">
            <div class="stat-icon">✅</div>
            <div class="stat-content">
              <div class="stat-value">{{ stats.terminees }}</div>
              <div class="stat-label">Terminées</div>
            </div>
          </div>
        </div>

        <div class="header-actions">
          <button @click="loadInterventions" :disabled="loading" class="refresh-btn">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            <span>Actualiser</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Filtres Ultra-Professionnels -->
    <div class="filters-section">
      <div class="filters-container">
        <div class="filter-group">
          <!-- Filtre Code Reçu -->
          <div class="filter-wrapper premium-filter code-filter" :class="{ 'active': filters.codeRecu }">
            <div class="filter-icon-container">
              <i class="fas fa-qrcode filter-icon"></i>
              <i v-if="filters.codeRecu" class="fas fa-check filter-check-icon"></i>
            </div>
            <input 
              type="text" 
              v-model="filters.codeRecu" 
              @input="applyFilters" 
              placeholder="Code reçu (ex: AAC86FA3)"
              class="filter-input premium-input"
              maxlength="8"
            >
            <button v-if="filters.codeRecu" @click="filters.codeRecu = ''; applyFilters()" class="clear-input-btn">
              <i class="fas fa-times"></i>
            </button>
            <div class="filter-badge premium-badge" v-if="filters.codeRecu">1</div>
            <div class="filter-shine"></div>
          </div>

          <!-- Filtre Statut Ultra-Premium -->
          <div class="filter-wrapper premium-filter" :class="getStatutFilterClass()">
            <div class="filter-icon-container">
              <i class="fas fa-flag-checkered filter-icon"></i>
              <i v-if="filters.statut" class="fas fa-check filter-check-icon"></i>
            </div>
            <select v-model="filters.statut" @change="applyFilters" class="filter-select premium-select">
              <option value="">Tous les statuts</option>
              <option value="Planifié">📅 Planifié</option>
              <option value="En cours">⚡ En cours</option>
              <option value="Terminé">✅ Terminé</option>
              <option value="Reporté">🔄 Reporté</option>
            </select>
            <i class="fas fa-chevron-down filter-arrow" :class="{ 'rotated': filters.statut }"></i>
            <div class="filter-badge premium-badge" v-if="filters.statut">1</div>
            <div class="filter-shine"></div>
          </div>

          <!-- Filtre Date Ultra-Premium -->
          <div class="date-filter-wrapper premium-filter" :class="getDateFilterClass()">
            <div class="filter-icon-container">
              <i class="fas fa-calendar-alt filter-icon"></i>
              <i v-if="filters.date" class="fas fa-check filter-check-icon"></i>
            </div>
            <input type="date" v-model="filters.date" @change="applyFilters" class="filter-date premium-date">
            <div class="filter-badge premium-badge" v-if="filters.date">1</div>
            <div class="filter-shine"></div>
          </div>

          <!-- Filtre Technicien Ultra-Premium -->
          <div class="filter-wrapper premium-filter" :class="getTechnicienFilterClass()" v-if="userRole === 'Admin' || userRole === 'Responsable Planning'">
            <div class="filter-icon-container">
              <i class="fas fa-user-cog filter-icon"></i>
              <i v-if="filters.technicien" class="fas fa-check filter-check-icon"></i>
            </div>
            <select v-model="filters.technicien" @change="applyFilters" class="filter-select premium-select">
              <option value="">Tous les techniciens</option>
              <option v-for="tech in techniciens" :key="tech._id" :value="tech._id">
                👨‍🔧 {{ tech.prenom }} {{ tech.nom }}
              </option>
            </select>
            <i class="fas fa-chevron-down filter-arrow" :class="{ 'rotated': filters.technicien }"></i>
            <div class="filter-badge premium-badge" v-if="filters.technicien">1</div>
            <div class="filter-shine"></div>
          </div>

          <!-- Filtre Commercial Ultra-Premium -->
          <div class="filter-wrapper premium-filter" :class="getCommercialFilterClass()" v-if="userRole === 'Admin' || userRole === 'Responsable Planning'">
            <div class="filter-icon-container">
              <i class="fas fa-briefcase filter-icon"></i>
              <i v-if="filters.commercial" class="fas fa-check filter-check-icon"></i>
            </div>
            <select v-model="filters.commercial" @change="applyFilters" class="filter-select premium-select">
              <option value="">Tous les commerciaux</option>
              <option v-for="comm in commerciaux" :key="comm._id" :value="comm._id">
                💼 {{ comm.prenom }} {{ comm.nom }}
              </option>
            </select>
            <i class="fas fa-chevron-down filter-arrow" :class="{ 'rotated': filters.commercial }"></i>
            <div class="filter-badge premium-badge" v-if="filters.commercial">1</div>
            <div class="filter-shine"></div>
          </div>

          <!-- Sélecteur de Vue Ultra-Premium -->
          <div class="view-selector premium-filter">
            <div class="view-toggle-group">
              <button 
                @click="viewMode = 'cards'" 
                :class="['view-toggle-btn', { 'active': viewMode === 'cards' }]"
                title="Vue en cartes"
              >
                <i class="fas fa-th-large"></i>
                <span>Cartes</span>
              </button>
              <button 
                @click="viewMode = 'table'" 
                :class="['view-toggle-btn', { 'active': viewMode === 'table' }]"
                title="Vue en tableau"
              >
                <i class="fas fa-table"></i>
                <span>Tableau</span>
              </button>
            </div>
          </div>

          <!-- Bouton Reset Ultra-Premium -->
          <button @click="resetFilters" class="reset-filters-btn premium-reset" :disabled="!hasActiveFilters">
            <div class="btn-icon-container">
              <i class="fas fa-undo-alt"></i>
            </div>
            <span>Reset</span>
            <div class="btn-shine"></div>
          </button>
        </div>
      </div>
    </div>

    <!-- Liste des Interventions -->
    <div class="interventions-content">
      <!-- État de chargement -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <h3>Chargement des operation...</h3>
        <p>Veuillez patienter pendant la récupération des données</p>
      </div>

      <!-- État vide -->
      <div v-else-if="!filteredInterventions.length" class="empty-state">
        <div class="empty-icon">🔍</div>
        <h3>Aucune intervention trouvée</h3>
        <p>Aucune intervention ne correspond à vos critères</p>
      </div>

      <!-- Vue en Cartes -->
      <div v-else-if="viewMode === 'cards'" class="interventions-grid">
        <div 
          v-for="intervention in paginatedInterventions" 
          :key="intervention._id" 
          class="intervention-card"
          :class="getCardStatusClass(intervention.statut)"
        >
          <!-- Header de la carte -->
          <div class="card-header">
            <div class="card-date">
              <div class="date-day">{{ getDay(intervention.dateRdv) }}</div>
              <div class="date-month">{{ getMonth(intervention.dateRdv) }}</div>
            </div>
            
            <div class="card-time">
              <i class="fas fa-clock"></i>
              <span>{{ intervention.heureDebut }} - {{ intervention.heureFin }}</span>
            </div>
            
            <div class="card-status">
              <div class="status-badge" :class="getStatusBadgeClass(intervention.statut)">
                {{ intervention.statut }}
              </div>
            </div>
          </div>

          <!-- Corps de la carte -->
          <div class="card-body">
            <!-- Type de mission et Prix -->
            <div class="card-mission-info">
              <div class="mission-type">
                <i class="fas fa-wrench"></i>
                <span>{{ intervention.prospect?.typeIntervention || 'Non défini' }}</span>
              </div>
              <div class="mission-price">
                <i class="fas fa-coins"></i>
                <span>{{ formatPrice(intervention.prospect?.prix) }} TND</span>
              </div>
            </div>

            <div class="card-client">
              <h4 class="client-name">
                <i class="fas fa-user"></i>
                {{ intervention.prospect?.client?.nomComplet || `${intervention.prospect?.client?.prenom || ''} ${intervention.prospect?.client?.nom || ''}`.trim() || 'Non renseigné' }}
              </h4>
              <div class="client-contact">
                <i class="fas fa-phone"></i>
                <span>{{ intervention.prospect?.client?.telephone }}</span>
              </div>
            </div>

            <div class="card-technicien">
              <i class="fas fa-user-cog"></i>
              <span>{{ intervention.technicien?.prenom }} {{ intervention.technicien?.nom }}</span>
            </div>
          </div>

          <!-- Actions de la carte -->
          <div class="card-actions">
            <button 
              v-if="userRole === 'Technicien' && canStartIntervention(intervention) && !['Installation', 'Intervention', 'Recouvrement'].includes(intervention.prospect?.typeIntervention)" 
              @click="startIntervention(intervention)"
              class="action-btn start-btn"
            >
              <i class="fas fa-play"></i>
              <span>Démarrer</span>
            </button>

            <button 
              v-if="userRole === 'Technicien' && (intervention.statut === 'Planifié' || intervention.statut === 'En cours')" 
              @click="reportIntervention(intervention)"
              class="action-btn report-btn"
            >
              <i class="fas fa-calendar-times"></i>
              <span>Report</span>
            </button>

            <button 
              v-if="userRole === 'Admin' && intervention.statut === 'Planifié'" 
              @click="reportInterventionAdmin(intervention)"
              class="action-btn report-btn-admin"
            >
              <i class="fas fa-user-edit"></i>
              <span>Reporter</span>
            </button>

            <button 
              v-if="userRole === 'Magasinier'" 
              @click="prepareStock(intervention)"
              class="action-btn stock-btn"
            >
              <i class="fas fa-boxes"></i>
              <span>Préparer Stock</span>
            </button>

            <!-- 
              LOGIQUE POUR TYPE INTERVENTION:
              - Si Appareils NON vide → Afficher les deux boutons: "📋 Check-list" ET "🏁 Terminer Intervention"
              - Si Appareils vide → Afficher seulement le bouton "📋 Check-list"
              - La mission ne doit PAS être Terminée pour valider les boutons
            -->
            
            <!-- Bouton Check-list pour les interventions de type "Intervention" (masqué si Terminé) -->
            <button 
              v-if="['Admin', 'Technicien'].includes(userRole) && intervention.prospect?.typeIntervention === 'Intervention' && !['Terminé', 'Annulé'].includes(intervention.statut)" 
              @click="openChecklistModal(intervention)"
              class="action-btn checklist-btn"
            >
              <i class="fas fa-clipboard-check"></i>
              <span>Check-list</span>
            </button>
            
            <!-- Bouton Terminer Intervention:
                 - Pour Installation/Prospect: toujours affiché si statut valide
                 - Pour Intervention: seulement si appareils NON vide
                 - Ne doit PAS être Terminé
            -->
            <button 
              v-if="['Admin', 'Technicien'].includes(userRole) && 
                    !['Terminé', 'Annulé'].includes(intervention.statut) &&
                    (intervention.prospect?.typeIntervention !== 'Intervention' || hasAppareils(intervention.prospect))" 
              @click="affectProduct(intervention)"
              class="action-btn affect-btn premium-affect-btn"
            >
              <div class="btn-icon-wrapper">
                <i class="fas fa-box btn-icon"></i>
                <div class="icon-glow"></div>
              </div>
              <span class="btn-text">Terminer Intervention</span>
              <div class="btn-particles">
                <div class="particle"></div>
                <div class="particle"></div>
                <div class="particle"></div>
              </div>
              <div class="btn-shine-effect"></div>
            </button>

            <button 
              @click="viewDetails(intervention)"
              class="action-btn view-btn"
            >
              <i class="fas fa-eye"></i>
              <span>Détails</span>
            </button>

            <!-- Bouton Supprimer (Admin uniquement) -->
            <button 
              v-if="userRole === 'Admin'"
              @click="confirmDeleteIntervention(intervention)"
              class="action-btn delete-btn"
              title="Supprimer cette intervention"
            >
              <i class="fas fa-trash"></i>
              <span>Supprimer</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Vue en Tableau Ultra-Professionnelle -->
      <div v-else class="interventions-table-container">
        <div class="table-wrapper">
          <table class="interventions-table">
            <thead class="table-header">
              <tr>
                <th class="th-date">
                  <i class="fas fa-calendar-day"></i>
                  <span>Date</span>
                </th>
                <th class="th-time">
                  <i class="fas fa-clock"></i>
                  <span>Horaires</span>
                </th>
                <th class="th-client">
                  <i class="fas fa-user"></i>
                  <span>Client</span>
                </th>
                <th class="th-type">
                  <i class="fas fa-wrench"></i>
                  <span>Type</span>
                </th>
                <th class="th-prix">
                  <i class="fas fa-coins"></i>
                  <span>Prix</span>
                </th>
                <th class="th-contact">
                  <i class="fas fa-phone"></i>
                  <span>Contact</span>
                </th>
                <th class="th-technicien">
                  <i class="fas fa-user-cog"></i>
                  <span>Technicien</span>
                </th>
                <th class="th-statut">
                  <i class="fas fa-flag"></i>
                  <span>Statut</span>
                </th>
                <th class="th-actions">
                  <i class="fas fa-cogs"></i>
                  <span>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody class="table-body">
              <tr 
                v-for="intervention in paginatedInterventions" 
                :key="intervention._id" 
                class="table-row"
                :class="getTableRowClass(intervention.statut)"
              >
                <td class="td-date">
                  <div class="date-cell">
                    <div class="date-day">{{ getDay(intervention.dateRdv) }}</div>
                    <div class="date-month">{{ getMonth(intervention.dateRdv) }}</div>
                  </div>
                </td>
                <td class="td-time">
                  <div class="time-cell">
                    <i class="fas fa-clock time-icon"></i>
                    <span>{{ intervention.heureDebut }} - {{ intervention.heureFin }}</span>
                  </div>
                </td>
                <td class="td-client">
                  <div class="client-cell">
                    <div class="client-name">
                      {{ intervention.prospect?.client?.nomComplet || `${intervention.prospect?.client?.prenom || ''} ${intervention.prospect?.client?.nom || ''}`.trim() || 'Non renseigné' }}
                    </div>
                  </div>
                </td>
                <td class="td-type">
                  <div class="type-cell">
                    <span class="type-badge-table">{{ intervention.prospect?.typeIntervention || 'N/A' }}</span>
                  </div>
                </td>
                <td class="td-prix">
                  <div class="prix-cell">
                    <span class="prix-badge-table">{{ formatPrice(intervention.prospect?.prix) }} TND</span>
                  </div>
                </td>
                <td class="td-contact">
                  <div class="contact-cell">
                    <a :href="`tel:${intervention.prospect?.client?.telephone}`" class="contact-link">
                      <i class="fas fa-phone"></i>
                      <span>{{ intervention.prospect?.client?.telephone }}</span>
                    </a>
                  </div>
                </td>
                <td class="td-technicien">
                  <div class="technicien-cell">
                    <i class="fas fa-user-cog"></i>
                    <span>{{ intervention.technicien?.prenom }} {{ intervention.technicien?.nom }}</span>
                  </div>
                </td>
                <td class="td-statut">
                  <div class="status-badge table-badge" :class="getStatusBadgeClass(intervention.statut)">
                    {{ intervention.statut }}
                  </div>
                </td>
                <td class="td-actions">
                  <div class="table-actions">
                    <button 
                      v-if="userRole === 'Technicien' && canStartIntervention(intervention) && !['Installation', 'Intervention', 'Recouvrement'].includes(intervention.prospect?.typeIntervention)" 
                      @click="startIntervention(intervention)"
                      class="table-action-btn start-btn"
                      title="Démarrer l'intervention"
                    >
                      <i class="fas fa-play"></i>
                    </button>

                    <button 
                      v-if="userRole === 'Technicien' && (intervention.statut === 'Planifié' || intervention.statut === 'En cours')" 
                      @click="reportIntervention(intervention)"
                      class="table-action-btn report-btn"
                      title="Reporter l'intervention"
                    >
                      <i class="fas fa-calendar-times"></i>
                    </button>

                    <button 
                      v-if="userRole === 'Magasinier'" 
                      @click="prepareStock(intervention)"
                      class="table-action-btn stock-btn"
                      title="Préparer le stock"
                    >
                      <i class="fas fa-boxes"></i>
                    </button>

                    <!-- Bouton Check-list pour les interventions de type "Intervention" (masqué si Terminé) -->
                    <button 
                      v-if="['Admin', 'Technicien'].includes(userRole) && intervention.prospect?.typeIntervention === 'Intervention' && !['Terminé', 'Annulé'].includes(intervention.statut)" 
                      @click="openChecklistModal(intervention)"
                      class="table-action-btn checklist-btn"
                      title="Check-list d'intervention"
                    >
                      <i class="fas fa-clipboard-check"></i>
                    </button>
                    
                    <!-- Bouton Terminer Intervention:
                         - Pour Installation/Prospect: toujours affiché si statut valide
                         - Pour Intervention: seulement si appareils NON vide
                    -->
                    <button 
                      v-if="['Admin', 'Technicien'].includes(userRole) && 
                            !['Terminé', 'Annulé'].includes(intervention.statut) &&
                            (intervention.prospect?.typeIntervention !== 'Intervention' || hasAppareils(intervention.prospect))" 
                      @click="affectProduct(intervention)"
                      class="table-action-btn affect-btn premium-table-affect-btn"
                      title="Terminer l'intervention complète"
                    >
                      <div class="table-btn-wrapper">
                        <i class="fas fa-box table-btn-icon"></i>
                        <div class="table-icon-glow"></div>
                      </div>
                      <div class="table-btn-ripple"></div>
                    </button>

                    <button 
                      @click="viewDetails(intervention)"
                      class="table-action-btn view-btn"
                      title="Voir les détails"
                    >
                      <i class="fas fa-eye"></i>
                    </button>

                    <!-- Bouton Supprimer (Admin uniquement) -->
                    <button 
                      v-if="userRole === 'Admin'"
                      @click="confirmDeleteIntervention(intervention)"
                      class="table-action-btn delete-btn"
                      title="Supprimer cette intervention"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section" v-if="filteredInterventions.length > 0">
      <div class="pagination-info">
        <span>Affichage {{ paginationStart }} - {{ paginationEnd }} sur {{ filteredInterventions.length }} opérations</span>
      </div>
      <div class="pagination-controls">
        <div class="items-per-page">
          <label>Par page:</label>
          <select v-model="itemsPerPage" @change="changeItemsPerPage" class="items-select">
            <option v-for="option in itemsPerPageOptions" :key="option" :value="option">{{ option }}</option>
          </select>
        </div>
        <div class="pagination-buttons">
          <button @click="goToPage(1)" :disabled="currentPage === 1" class="pagination-btn">
            ⏮️
          </button>
          <button @click="previousPage" :disabled="currentPage === 1" class="pagination-btn">
            ◀️ Précédent
          </button>
          <div class="page-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page" 
              @click="goToPage(page)"
              :class="['page-btn', { active: currentPage === page }]"
            >
              {{ page }}
            </button>
          </div>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
            Suivant ▶️
          </button>
          <button @click="goToPage(totalPages)" :disabled="currentPage === totalPages" class="pagination-btn">
            ⏭️
          </button>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <StockPreparationModal v-if="showStockModal" :intervention="selectedIntervention" 
                          @close="showStockModal = false" />
    <InterventionDetailModal v-if="showDetailModal" :intervention="selectedIntervention" 
                            @close="showDetailModal = false" />
    <CompletInterventionModal :show="showAffectModal" :intervention="selectedIntervention" 
                            @close="showAffectModal = false" 
                            @intervention-completed="handleInterventionCompleted" />
    <ReportInterventionModal v-if="showReportModal" :show="showReportModal" :intervention="selectedIntervention" 
                            @close="showReportModal = false" 
                            @reported="handleInterventionReported" />

    <!-- Modal de confirmation de suppression -->
    <div v-if="showDeleteConfirmModal" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content delete-confirm-modal" @click.stop>
        <div class="modal-header delete-header">
          <h4>
            <i class="fas fa-exclamation-triangle"></i>
            Confirmer la suppression
          </h4>
          <button @click="cancelDelete" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="delete-warning">
            <div class="warning-icon">
              <i class="fas fa-trash-alt"></i>
            </div>
            <div class="warning-content">
              <p class="warning-title">Êtes-vous sûr de vouloir supprimer cette intervention ?</p>
              <div class="intervention-info" v-if="interventionToDelete">
                <p><strong>Client:</strong> {{ interventionToDelete.prospect?.client?.nomComplet || 'Non renseigné' }}</p>
                <p><strong>Date:</strong> {{ formatDate(interventionToDelete.dateRdv) }}</p>
                <p><strong>Type:</strong> {{ interventionToDelete.prospect?.typeIntervention }}</p>
                <p><strong>Statut:</strong> {{ interventionToDelete.statut }}</p>
              </div>
              <p class="warning-text">⚠️ Cette action est irréversible. Toutes les données associées seront supprimées.</p>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="cancelDelete" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Annuler
          </button>
          <button @click="deleteIntervention" class="btn btn-danger" :disabled="deletingIntervention">
            <i v-if="deletingIntervention" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-trash"></i>
            {{ deletingIntervention ? 'Suppression...' : 'Supprimer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Check-list d'intervention -->
    <div v-if="showChecklistModal" class="modal-overlay" @click="closeChecklistModal">
      <div class="modal-content checklist-modal" @click.stop>
        <div class="modal-header">
          <h4>
            <i class="fas fa-clipboard-check"></i>
            Check-list d'intervention
          </h4>
          <button @click="closeChecklistModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="checklist-section">
            <h5><i class="fas fa-tools"></i> Motif d'intervention</h5>
            <div class="checklist-items">
              <div v-for="item in checklist.technical" :key="item.id" class="checklist-item">
                <input type="checkbox" :id="item.id" v-model="item.completed">
                <label :for="item.id">{{ item.label }}</label>
              </div>
            </div>
          </div>
          
          <div class="checklist-progress">
            <div class="progress-info">
              <span>Sélection: {{ checklistProgress.completed }} motif(s)</span>
              <span class="progress-percent" :class="{ 'valid': checklistProgress.completed > 0 }">
                {{ checklistProgress.completed > 0 ? '✓ Valide' : 'Cochez au moins 1' }}
              </span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: checklistProgress.completed > 0 ? '100%' : '0%' }"></div>
            </div>
          </div>
          
          <!-- Section Photos d'Intervention -->
          <div class="photos-section">
            <h5><i class="fas fa-camera"></i> Photos d'Intervention</h5>
            
            <!-- Photos Avant Intervention -->
            <div class="photo-upload-container">
              <div class="upload-header">
                <div class="upload-icon before">
                  <i class="fas fa-camera"></i>
                </div>
                <div class="upload-info">
                  <h6>Photos Avant Intervention</h6>
                  <p>Capturez l'état initial du site</p>
                </div>
              </div>
              
              <div class="upload-zone" 
                   @click="$refs.beforePhotosInput.click()"
                   @dragover.prevent="dragActive.before = true"
                   @dragleave.prevent="dragActive.before = false"
                   @drop.prevent="handlePhotoDrop('before', $event)"
                   :class="{ 'drag-active': dragActive.before }">
                <input ref="beforePhotosInput" 
                       type="file" 
                       multiple 
                       accept="image/*" 
                       @change="handlePhotoUpload('before', $event)"
                       class="hidden-input">
                
                <div class="upload-content" v-if="photos.before.length === 0">
                  <i class="fas fa-cloud-upload-alt upload-icon-large"></i>
                  <span class="upload-text">Cliquez ou glissez vos images ici</span>
                  <span class="upload-hint">PNG, JPG, JPEG jusqu'à 10MB chacune</span>
                  <button type="button" class="upload-btn">
                    <i class="fas fa-plus"></i> Ajouter des photos
                  </button>
                </div>
                
                <div class="photo-preview-grid" v-else>
                  <div v-for="(photo, index) in photos.before" :key="'before-' + index" class="photo-preview-item">
                    <img :src="photo" alt="Photo avant">
                    <button type="button" @click.stop="removePhoto('before', index)" class="remove-photo-btn">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <div class="add-more-photo" @click.stop="$refs.beforePhotosInput.click()">
                    <i class="fas fa-plus"></i>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Photos Après Intervention -->
            <div class="photo-upload-container">
              <div class="upload-header">
                <div class="upload-icon after">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="upload-info">
                  <h6>Photos Après Intervention</h6>
                  <p>Documentez le travail accompli</p>
                </div>
              </div>
              
              <div class="upload-zone" 
                   @click="$refs.afterPhotosInput.click()"
                   @dragover.prevent="dragActive.after = true"
                   @dragleave.prevent="dragActive.after = false"
                   @drop.prevent="handlePhotoDrop('after', $event)"
                   :class="{ 'drag-active': dragActive.after }">
                <input ref="afterPhotosInput" 
                       type="file" 
                       multiple 
                       accept="image/*" 
                       @change="handlePhotoUpload('after', $event)"
                       class="hidden-input">
                
                <div class="upload-content" v-if="photos.after.length === 0">
                  <i class="fas fa-cloud-upload-alt upload-icon-large"></i>
                  <span class="upload-text">Cliquez ou glissez vos images ici</span>
                  <span class="upload-hint">PNG, JPG, JPEG jusqu'à 10MB chacune</span>
                  <button type="button" class="upload-btn">
                    <i class="fas fa-plus"></i> Ajouter des photos
                  </button>
                </div>
                
                <div class="photo-preview-grid" v-else>
                  <div v-for="(photo, index) in photos.after" :key="'after-' + index" class="photo-preview-item">
                    <img :src="photo" alt="Photo après">
                    <button type="button" @click.stop="removePhoto('after', index)" class="remove-photo-btn">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <div class="add-more-photo" @click.stop="$refs.afterPhotosInput.click()">
                    <i class="fas fa-plus"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeChecklistModal" class="btn btn-secondary">Fermer</button>
          <button @click="saveChecklist" class="btn btn-primary" :disabled="savingChecklist">
            <i v-if="savingChecklist" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ checklistProgress.percent === 100 ? 'Terminer l\'intervention' : 'Sauvegarder' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Admin - Reporter et Réaffecter -->
    <div v-if="showAdminReportModal" class="modal-overlay" @click="closeAdminReportModal">
      <div class="modal-content admin-report-modal" @click.stop>
        <div class="modal-header">
          <h4>
            <i class="fas fa-user-edit"></i>
            Reporter et Réaffecter l'Intervention
          </h4>
          <button @click="closeAdminReportModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Informations de l'intervention -->
          <div class="intervention-summary">
            <h5>{{ selectedIntervention?.prospect?.client?.nomComplet || 'Client' }}</h5>
            <p>{{ selectedIntervention?.prospect?.typeIntervention || 'Intervention' }}</p>
          </div>

          <!-- Informations actuelles -->
          <div class="current-info-card">
            <h6><i class="fas fa-calendar-alt"></i> Informations Actuelles</h6>
            <div class="info-details">
              <div class="info-item">
                <span class="label">Date :</span>
                <span class="value">{{ formatDate(selectedIntervention?.dateRdv) }}</span>
              </div>
              <div class="info-item">
                <span class="label">Horaire :</span>
                <span class="value">{{ selectedIntervention?.heureDebut }} - {{ selectedIntervention?.heureFin }}</span>
              </div>
              <div class="info-item">
                <span class="label">Technicien :</span>
                <span class="value">{{ selectedIntervention?.technicien?.prenom }} {{ selectedIntervention?.technicien?.nom }}</span>
              </div>
            </div>
          </div>

          <!-- Modification de la date et horaire -->
          <div class="date-modification-section">
            <h6>
              <i class="fas fa-calendar-edit"></i>
              Nouvelle Date et Horaire
            </h6>
            
            <div class="date-inputs-grid">
              <div class="form-group">
                <label for="admin-nouvelle-date">Date *</label>
                <input 
                  id="admin-nouvelle-date"
                  type="date" 
                  v-model="nouvelleDate"
                  class="form-control"
                  :min="minDate"
                  required
                >
              </div>
              
              <div class="form-group">
                <label for="admin-heure-debut">Heure Début *</label>
                <input 
                  id="admin-heure-debut"
                  type="time" 
                  v-model="nouvelleHeureDebut"
                  class="form-control"
                  required
                >
              </div>
              
              <div class="form-group">
                <label for="admin-heure-fin">Heure Fin *</label>
                <input 
                  id="admin-heure-fin"
                  type="time" 
                  v-model="nouvelleHeureFin"
                  class="form-control"
                  required
                >
              </div>
            </div>
          </div>

          <!-- Sélection du nouveau technicien -->
          <div class="form-group">
            <label for="admin-technicien-select">
              <i class="fas fa-user"></i>
              Nouveau Technicien *
            </label>
            <select 
              id="admin-technicien-select"
              v-model="selectedTechnicien" 
              class="form-control"
              required
            >
              <option value="">-- Choisir un technicien --</option>
              <option 
                v-for="tech in techniciens" 
                :key="tech._id" 
                :value="tech._id"
              >
                {{ tech.prenom }} {{ tech.nom }}
              </option>
            </select>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeAdminReportModal" class="btn btn-secondary">
            <i class="fas fa-arrow-left"></i>
            Annuler
          </button>
          <button 
            @click="confirmAdminReport" 
            class="btn btn-primary"
            :disabled="!selectedTechnicien || !nouvelleDate || !nouvelleHeureDebut || !nouvelleHeureFin"
          >
            <i class="fas fa-check"></i>
            Reporter et Réaffecter
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import StockPreparationModal from './StockPreparationModal.vue'
import InterventionDetailModal from './InterventionDetailModal.vue'
import CompletInterventionModal from './planning/CompletInterventionModal.vue'
import ReportInterventionModal from './interventions/ReportInterventionModal.vue'

export default {
  name: 'InterventionsManager',
  components: { StockPreparationModal, InterventionDetailModal, CompletInterventionModal, ReportInterventionModal },
  props: {
    userRole: String,
    userId: String
  },
  setup(props) {
    const toast = useToast()
    const loading = ref(false)
    const interventions = ref([])
    const selectedIntervention = ref(null)
    const showStockModal = ref(false)
    const showDetailModal = ref(false)
    const showAffectModal = ref(false)
    const showReportModal = ref(false)
    const showAdminReportModal = ref(false)
    const showChecklistModal = ref(false)
    const showDeleteConfirmModal = ref(false)
    const interventionToDelete = ref(null)
    const deletingIntervention = ref(false)
    const savingChecklist = ref(false)
    
    // État de validation pour les interventions de type "Intervention" avec appareils
    // Pour clôturer: Check-list ET Terminer Intervention doivent être validés
    const interventionValidationState = ref({})  // { interventionId: { checklistValidated: bool, terminateValidated: bool } }
    
    // Check-list pour les interventions de type "Intervention" - Au moins un élément doit être coché
    const checklist = ref({
      technical: [
        { id: 'ancien_boitier', label: 'Ancien boitier', completed: false },
        { id: 'migration', label: 'Migration', completed: false },
        { id: 'sim_injoignable', label: 'SIM injoignable', completed: false },
        { id: 'pas_solde_internet', label: 'Pas de solde internet', completed: false },
        { id: 'pas_solde_sms', label: 'Pas de solde SMS', completed: false },
        { id: 'expiration_abonnement', label: 'Expiration Abonnement', completed: false },
        { id: 'pas_courant_boitier', label: 'Pas de courant au niveau de boitier', completed: false },
        { id: 'boitier_defectueux', label: 'Boitier défectueux', completed: false },
        { id: 'login_mot_passe', label: 'Login ou Mot de passe', completed: false },
        { id: 'probleme_notification', label: 'Problème notification', completed: false },
        { id: 'manque_evenements', label: 'Manque d\'événements', completed: false },
        { id: 'absence_historique', label: 'Absence d\'historique', completed: false },
        { id: 'numero_sos', label: 'Numéro SOS', completed: false },
        { id: 'autres', label: 'Autres...', completed: false }
      ]
    })
    
    // Photos d'intervention
    const photos = ref({
      before: [],
      after: []
    })
    
    const dragActive = ref({
      before: false,
      after: false
    })
    
    // Progression de la check-list
    const checklistProgress = computed(() => {
      const completed = checklist.value.technical.filter(item => item.completed).length
      const total = checklist.value.technical.length
      return {
        completed,
        total,
        percent: Math.round((completed / total) * 100)
      }
    })
    const filters = ref({ statut: '', date: '', technicien: '', commercial: '', codeRecu: '' })
    const viewMode = ref('cards')
    const isMobile = ref(window.innerWidth <= 768)

    // Pagination
    const currentPage = ref(1)
    const itemsPerPage = ref(10)
    const itemsPerPageOptions = [5, 10, 20, 50]

    // Détecter le mode mobile et forcer les cartes
    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768
      if (isMobile.value) {
        viewMode.value = 'cards'
      }
    }
    
    // Variables pour le modal admin
    const techniciens = ref([])
    const commerciaux = ref([])
    const selectedTechnicien = ref('')
    const nouvelleDate = ref('')
    const nouvelleHeureDebut = ref('')
    const nouvelleHeureFin = ref('')
    
    // Date minimum (aujourd'hui)
    const minDate = computed(() => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    })

    const stats = computed(() => ({
      planifiees: interventions.value.filter(i => i.statut === 'Planifié').length,
      enCours: interventions.value.filter(i => i.statut === 'En cours').length,
      terminees: interventions.value.filter(i => i.statut === 'Terminé').length
    }))

    const filteredInterventions = computed(() => {
      let result = interventions.value
      if (props.userRole === 'Technicien') {
        result = result.filter(i => i.technicien?._id === props.userId)
      }
      // Filtre par code de reçu (recherche par les 8 derniers caractères de l'ID)
      if (filters.value.codeRecu) {
        const searchCode = filters.value.codeRecu.toUpperCase().trim()
        result = result.filter(i => {
          const recuCode = i._id.toString().slice(-8).toUpperCase()
          return recuCode.includes(searchCode)
        })
      }
      if (filters.value.statut) {
        // Filtre par statut spécifique
        result = result.filter(i => i.statut === filters.value.statut)
      } else {
        // "Tous les statuts" = Planifié, Terminé, Reporté uniquement
        result = result.filter(i => ['Planifié', 'Terminé', 'Reporté'].includes(i.statut))
      }
      if (filters.value.date) {
        result = result.filter(i => new Date(i.dateRdv).toISOString().split('T')[0] === filters.value.date)
      }
      // Filtre par technicien
      if (filters.value.technicien) {
        result = result.filter(i => i.technicien?._id === filters.value.technicien)
      }
      // Filtre par commercial
      if (filters.value.commercial) {
        result = result.filter(i => i.prospect?.commercial?._id === filters.value.commercial)
      }
      // Tri par date (du plus récent au plus ancien)
      return result.sort((a, b) => new Date(b.dateRdv) - new Date(a.dateRdv))
    })

    // Pagination computed
    const totalPages = computed(() => 
      Math.ceil(filteredInterventions.value.length / itemsPerPage.value)
    )
    
    const paginatedInterventions = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredInterventions.value.slice(start, end)
    })
    
    const paginationStart = computed(() => 
      filteredInterventions.value.length === 0 ? 0 : (currentPage.value - 1) * itemsPerPage.value + 1
    )
    
    const paginationEnd = computed(() => 
      Math.min(currentPage.value * itemsPerPage.value, filteredInterventions.value.length)
    )
    
    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      
      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        if (current <= 3) {
          for (let i = 1; i <= 5; i++) pages.push(i)
        } else if (current >= total - 2) {
          for (let i = total - 4; i <= total; i++) pages.push(i)
        } else {
          for (let i = current - 2; i <= current + 2; i++) pages.push(i)
        }
      }
      return pages
    })

    // Méthodes pagination
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
      }
    }
    
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
      }
    }
    
    const previousPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
      }
    }
    
    const changeItemsPerPage = () => {
      currentPage.value = 1
    }

    const loadInterventions = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/rdv?limit=100', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          // Filtrer les interventions annulées uniquement pour les techniciens
          if (props.userRole === 'Technicien') {
            interventions.value = (data.rdvs || []).filter(rdv => rdv.statut !== 'Annulé')
          } else {
            interventions.value = data.rdvs || []
          }
        }
      } catch (error) {
        toast.error('Erreur de chargement')
      } finally {
        loading.value = false
      }
    }

    const applyFilters = () => {}
    const resetFilters = () => { filters.value = { statut: '', date: '', technicien: '', commercial: '', codeRecu: '' } }
    const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR')
    const canStartIntervention = (intervention) => intervention.statut === 'Planifié' && intervention.technicien?._id === props.userId

    const startIntervention = async (intervention) => {
      try {
        const token = localStorage.getItem('token')
        await fetch(`/api/rdv/${intervention._id}/demarrer`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        })
        toast.success('Intervention démarrée')
        loadInterventions()
      } catch (error) {
        toast.error('Erreur')
      }
    }

    const prepareStock = (intervention) => {
      selectedIntervention.value = intervention
      showStockModal.value = true
    }

    const viewDetails = (intervention) => {
      selectedIntervention.value = intervention
      showDetailModal.value = true
    }

    // Fonction pour confirmer la suppression d'une intervention
    const confirmDeleteIntervention = (intervention) => {
      interventionToDelete.value = intervention
      showDeleteConfirmModal.value = true
    }

    // Fonction pour supprimer une intervention
    const deleteIntervention = async () => {
      if (!interventionToDelete.value) return
      
      deletingIntervention.value = true
      
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/rdv/${interventionToDelete.value._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.ok) {
          toast.success('✅ Intervention supprimée avec succès')
          showDeleteConfirmModal.value = false
          interventionToDelete.value = null
          loadInterventions()
        } else {
          const data = await response.json()
          toast.error(data.message || 'Erreur lors de la suppression')
        }
      } catch (error) {
        console.error('Erreur suppression intervention:', error)
        toast.error('Erreur lors de la suppression de l\'intervention')
      } finally {
        deletingIntervention.value = false
      }
    }

    // Annuler la suppression
    const cancelDelete = () => {
      showDeleteConfirmModal.value = false
      interventionToDelete.value = null
    }

    const affectProduct = (intervention) => {
      const prospect = intervention.prospect
      const isInterventionType = prospect?.typeIntervention === 'Intervention'
      const hasAppareilsData = hasAppareils(prospect)
      
      // Pour les interventions de type "Intervention" avec appareils,
      // vérifier que la check-list a été validée avant de permettre "Terminer Intervention"
      if (isInterventionType && hasAppareilsData) {
        const interventionId = intervention._id
        const validationState = interventionValidationState.value[interventionId]
        
        // Vérifier aussi si le RDV a déjà la check-list validée (depuis le backend)
        const checklistAlreadyValidated = intervention.checklistValidated === true
        
        if (!validationState?.checklistValidated && !checklistAlreadyValidated) {
          toast.warning('⚠️ Veuillez d\'abord valider la Check-list avant de terminer l\'intervention.')
          return
        }
      }
      
      selectedIntervention.value = intervention
      showAffectModal.value = true
    }

    const handleInterventionCompleted = () => {
      showAffectModal.value = false
      
      // Nettoyer l'état de validation pour cette intervention
      if (selectedIntervention.value?._id) {
        delete interventionValidationState.value[selectedIntervention.value._id]
      }
      
      toast.success('🎉 Intervention terminée avec succès !')
      loadInterventions()
    }

    const reportIntervention = (intervention) => {
      selectedIntervention.value = intervention
      showReportModal.value = true
    }

    const handleInterventionReported = () => {
      showReportModal.value = false
      loadInterventions()
    }

    // Méthodes pour le modal Check-list
    const openChecklistModal = (intervention) => {
      selectedIntervention.value = intervention
      
      // Charger les données de check-list sauvegardées si elles existent
      if (intervention.checklistValidation && intervention.checklistValidation.length > 0) {
        // Restaurer l'état de la check-list depuis les données sauvegardées
        checklist.value.technical.forEach(item => {
          const savedItem = intervention.checklistValidation.find(saved => saved.id === item.id)
          if (savedItem) {
            item.completed = savedItem.completed || savedItem.valide || false
          } else {
            item.completed = false
          }
        })
        console.log('✅ Check-list chargée depuis les données sauvegardées:', checklist.value.technical)
      } else {
        // Réinitialiser la check-list si pas de données sauvegardées
        checklist.value.technical.forEach(item => item.completed = false)
        console.log('ℹ️ Pas de données de check-list sauvegardées, réinitialisation')
      }
      
      // Charger les photos sauvegardées si elles existent
      if (intervention.photosIntervention && intervention.photosIntervention.length > 0) {
        photos.value.before = intervention.photosIntervention
          .filter(p => p.type === 'avant')
          .map(p => p.data)
        photos.value.after = intervention.photosIntervention
          .filter(p => p.type === 'apres')
          .map(p => p.data)
        console.log('✅ Photos chargées:', { before: photos.value.before.length, after: photos.value.after.length })
      } else {
        // Réinitialiser les photos si pas de données sauvegardées
        photos.value = { before: [], after: [] }
      }
      
      dragActive.value = { before: false, after: false }
      showChecklistModal.value = true
    }

    const closeChecklistModal = () => {
      showChecklistModal.value = false
      selectedIntervention.value = null
      photos.value = { before: [], after: [] }
    }
    
    // Méthodes pour gérer les photos
    const handlePhotoUpload = (type, event) => {
      const files = event.target.files
      if (files) {
        Array.from(files).forEach(file => {
          if (file.size > 10 * 1024 * 1024) {
            toast.warning(`Le fichier ${file.name} dépasse 10MB`)
            return
          }
          const reader = new FileReader()
          reader.onload = (e) => {
            photos.value[type].push(e.target.result)
          }
          reader.readAsDataURL(file)
        })
      }
    }
    
    const handlePhotoDrop = (type, event) => {
      dragActive.value[type] = false
      const files = event.dataTransfer.files
      if (files) {
        Array.from(files).forEach(file => {
          if (!file.type.startsWith('image/')) {
            toast.warning(`Le fichier ${file.name} n'est pas une image`)
            return
          }
          if (file.size > 10 * 1024 * 1024) {
            toast.warning(`Le fichier ${file.name} dépasse 10MB`)
            return
          }
          const reader = new FileReader()
          reader.onload = (e) => {
            photos.value[type].push(e.target.result)
          }
          reader.readAsDataURL(file)
        })
      }
    }
    
    const removePhoto = (type, index) => {
      photos.value[type].splice(index, 1)
    }

    const saveChecklist = async () => {
      // Vérifier si au moins un élément de la check-list est coché
      const atLeastOneCompleted = checklist.value.technical.some(item => item.completed)
      
      if (!atLeastOneCompleted) {
        // Aucun élément coché
        toast.warning(`⚠️ Veuillez cocher au moins un motif d'intervention.`)
        return
      }
      
      // Check-list complète
      savingChecklist.value = true
      
      try {
        const token = localStorage.getItem('token')
        const interventionId = selectedIntervention.value._id
        const prospect = selectedIntervention.value.prospect
        
        // Vérifier si cette intervention de type "Intervention" a des appareils
        const isInterventionType = prospect?.typeIntervention === 'Intervention'
        const hasAppareilsData = hasAppareils(prospect)
        
        // Préparer les photos pour l'envoi
        const photosIntervention = []
        photos.value.before.forEach(photoData => {
          photosIntervention.push({
            data: photoData,
            type: 'avant',
            timestamp: new Date()
          })
        })
        photos.value.after.forEach(photoData => {
          photosIntervention.push({
            data: photoData,
            type: 'apres',
            timestamp: new Date()
          })
        })
        
        // Si c'est une intervention avec appareils, on ne clôture PAS directement
        // On marque seulement la check-list comme validée
        if (isInterventionType && hasAppareilsData) {
          // Initialiser l'état de validation si nécessaire
          if (!interventionValidationState.value[interventionId]) {
            interventionValidationState.value[interventionId] = {
              checklistValidated: false,
              terminateValidated: false
            }
          }
          
          // Marquer la check-list comme validée
          interventionValidationState.value[interventionId].checklistValidated = true
          
          // Sauvegarder la check-list sans clôturer (statut reste inchangé)
          const response = await fetch(`/api/rdv/${interventionId}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              // Ne pas changer le statut, juste sauvegarder les données
              checklistValidation: checklist.value.technical,
              photosIntervention: photosIntervention,
              checklistValidated: true  // Flag pour indiquer que la check-list est validée
            })
          })
          
          if (response.ok) {
            toast.success('✅ Check-list validée ! Veuillez maintenant cliquer sur "Terminer Intervention" pour finaliser.')
            closeChecklistModal()
            loadInterventions()
          } else {
            const errorData = await response.json()
            console.error('Erreur API:', errorData)
            toast.error('Erreur lors de la sauvegarde de la check-list')
          }
        } else {
          // Pour les interventions SANS appareils, on peut clôturer directement
          const response = await fetch(`/api/rdv/${interventionId}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              statut: 'Terminé',
              timestamp: new Date(),
              checklistValidation: checklist.value.technical,
              photosIntervention: photosIntervention
            })
          })
          
          if (response.ok) {
            toast.success('✅ Intervention Terminée avec succès')
            closeChecklistModal()
            loadInterventions()
          } else {
            const errorData = await response.json()
            console.error('Erreur API:', errorData)
            toast.error('Erreur lors de la clôture de l\'intervention')
          }
        }
      } catch (error) {
        console.error('Erreur sauvegarde check-list:', error)
        toast.error('Erreur lors de la sauvegarde')
      } finally {
        savingChecklist.value = false
      }
    }

    // Fonction pour l'admin - Reporter et réaffecter
    const reportInterventionAdmin = (intervention) => {
      selectedIntervention.value = intervention
      
      // Pré-remplir les champs avec les valeurs actuelles
      if (intervention.dateRdv) {
        const rdvDate = new Date(intervention.dateRdv)
        nouvelleDate.value = rdvDate.toISOString().split('T')[0]
      }
      nouvelleHeureDebut.value = intervention.heureDebut || ''
      nouvelleHeureFin.value = intervention.heureFin || ''
      selectedTechnicien.value = ''
      
      // Charger les techniciens
      loadTechniciens()
      
      showAdminReportModal.value = true
    }

    const closeAdminReportModal = () => {
      showAdminReportModal.value = false
      selectedIntervention.value = null
      selectedTechnicien.value = ''
      nouvelleDate.value = ''
      nouvelleHeureDebut.value = ''
      nouvelleHeureFin.value = ''
    }

    const confirmAdminReport = async () => {
      if (!selectedTechnicien.value) {
        toast.warning('⚠️ Veuillez sélectionner un technicien')
        return
      }

      if (!nouvelleDate.value || !nouvelleHeureDebut.value || !nouvelleHeureFin.value) {
        toast.warning('⚠️ Veuillez renseigner la date et les horaires')
        return
      }

      try {
        const token = localStorage.getItem('token')
        
        const response = await fetch(`/api/rdv/${selectedIntervention.value._id}/reporter-admin`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            nouveauTechnicien: selectedTechnicien.value,
            nouvelleDate: nouvelleDate.value,
            nouvelleHeureDebut: nouvelleHeureDebut.value,
            nouvelleHeureFin: nouvelleHeureFin.value
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Erreur lors du report')
        }

        toast.success('✅ Intervention reportée et technicien réaffecté avec succès')
        closeAdminReportModal()
        loadInterventions()
        
      } catch (error) {
        console.error('Erreur lors du report admin:', error)
        toast.error(`❌ ${error.message}`)
      }
    }

    // Charger la liste des techniciens
    const loadTechniciens = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/prospects/techniciens', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          techniciens.value = result.techniciens || []
        }
      } catch (error) {
        console.error('Erreur lors du chargement des techniciens:', error)
      }
    }

    // Charger la liste des commerciaux
    const loadCommerciaux = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/prospects/commerciaux', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          commerciaux.value = result.commerciaux || []
        }
      } catch (error) {
        console.error('Erreur lors du chargement des commerciaux:', error)
      }
    }

    /**
     * Vérifie si le prospect a des appareils assignés
     * Pour les missions de type "Intervention":
     * - Si Appareils NON vide → true (afficher les deux boutons)
     * - Si Appareils vide → false (afficher seulement Check-list)
     */
    const hasAppareils = (prospect) => {
      if (!prospect) return false
      
      // Vérifier les catégories (nouveau format)
      if (prospect.categories && Array.isArray(prospect.categories) && prospect.categories.length > 0) {
        const hasValidCategories = prospect.categories.some(cat => cat.nom && cat.quantite && cat.quantite > 0)
        if (hasValidCategories) return true
      }
      
      // Vérifier les appareils (format de compatibilité)
      if (prospect.appareils && Array.isArray(prospect.appareils) && prospect.appareils.length > 0) {
        const hasValidAppareils = prospect.appareils.some(app => app.type && app.quantite && app.quantite > 0)
        if (hasValidAppareils) return true
      }
      
      // Vérifier les champs de compatibilité (ancienne version)
      if (prospect.typeAppareil && prospect.nombreAppareils && prospect.nombreAppareils > 0) {
        return true
      }
      
      // Fallback: juste le type d'appareil
      if (prospect.typeAppareil && prospect.typeAppareil.trim() !== '') {
        return true
      }
      
      return false
    }

    // Nouvelles méthodes pour le design professionnel
    const getCardStatusClass = (statut) => {
      switch (statut) {
        case 'Planifié': return 'status-planned'
        case 'En cours': return 'status-progress'
        case 'Terminé': return 'status-completed'
        case 'Reporté': return 'status-reported'
        default: return 'status-default'
      }
    }

    const getStatusBadgeClass = (statut) => {
      switch (statut) {
        case 'Planifié': return 'badge-planned'
        case 'En cours': return 'badge-progress'
        case 'Terminé': return 'badge-completed'
        case 'Reporté': return 'badge-reported'
        default: return 'badge-default'
      }
    }

    const getTableRowClass = (statut) => {
      switch (statut) {
        case 'Planifié': return 'row-planned'
        case 'En cours': return 'row-progress'
        case 'Terminé': return 'row-completed'
        case 'Reporté': return 'row-reported'
        default: return 'row-default'
      }
    }

    const getDay = (date) => {
      return new Date(date).getDate().toString().padStart(2, '0')
    }

    const getMonth = (date) => {
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
      return months[new Date(date).getMonth()]
    }

    const formatPrice = (prix) => {
      if (prix === null || prix === undefined) return '0,00'
      return Number(prix).toFixed(2).replace('.', ',')
    }

    // Nouvelles méthodes pour les filtres ultra-professionnels
    const getStatutFilterClass = () => {
      return {
        'has-selection': filters.value.statut,
        'filter-active': filters.value.statut
      }
    }

    const getDateFilterClass = () => {
      return {
        'has-selection': filters.value.date,
        'filter-active': filters.value.date
      }
    }

    const getTechnicienFilterClass = () => {
      return {
        'has-selection': filters.value.technicien,
        'filter-active': filters.value.technicien
      }
    }

    const getCommercialFilterClass = () => {
      return {
        'has-selection': filters.value.commercial,
        'filter-active': filters.value.commercial
      }
    }

    const hasActiveFilters = computed(() => {
      return filters.value.statut || filters.value.date || filters.value.technicien || filters.value.commercial || filters.value.codeRecu
    })

    onMounted(() => {
      // Pour les techniciens, filtrer automatiquement sur "Planifié" par défaut
      if (props.userRole === 'Technicien') {
        filters.value.statut = 'Planifié'
      }
      
      loadInterventions()
      
      // Charger les techniciens et commerciaux pour les filtres (Admin et Responsable Planning)
      if (props.userRole === 'Admin' || props.userRole === 'Responsable Planning') {
        loadTechniciens()
        loadCommerciaux()
      }
      
      // Initialiser le mode responsive
      handleResize()
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      loading, interventions, selectedIntervention, showStockModal, showDetailModal, showAffectModal, showReportModal,
      filters, stats, filteredInterventions, paginatedInterventions, loadInterventions, applyFilters,
      resetFilters, formatDate, canStartIntervention, startIntervention,
      prepareStock, viewDetails, affectProduct, handleInterventionCompleted,
      reportIntervention, handleInterventionReported,
      getCardStatusClass, getStatusBadgeClass, getTableRowClass, getDay, getMonth, formatPrice,
      getStatutFilterClass, getDateFilterClass, getTechnicienFilterClass, getCommercialFilterClass, hasActiveFilters, viewMode,
      // Pagination
      currentPage, itemsPerPage, itemsPerPageOptions, totalPages,
      paginationStart, paginationEnd, visiblePages,
      goToPage, nextPage, previousPage, changeItemsPerPage,
      // Nouvelles variables et fonctions pour l'admin
      showAdminReportModal, reportInterventionAdmin, closeAdminReportModal, confirmAdminReport,
      techniciens, commerciaux, selectedTechnicien, nouvelleDate, nouvelleHeureDebut, nouvelleHeureFin, minDate,
      // Variables et fonctions pour le modal Check-list
      showChecklistModal, checklist, checklistProgress, savingChecklist,
      openChecklistModal, closeChecklistModal, saveChecklist,
      // Variables et fonctions pour les photos
      photos, dragActive, handlePhotoUpload, handlePhotoDrop, removePhoto,
      isMobile,
      // Fonction pour vérifier si le prospect a des appareils
      hasAppareils,
      // État de validation pour les interventions avec appareils
      interventionValidationState,
      // Variables et fonctions pour la suppression
      showDeleteConfirmModal, interventionToDelete, deletingIntervention,
      confirmDeleteIntervention, deleteIntervention, cancelDelete
    }
  }
}
</script>

<style scoped>
.interventions-manager {
  padding: 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 100vh;
}
.interventions-header {
  position: relative;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 27, 183, 0.2);
}

.header-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%);
}

.header-content {
  position: relative; z-index: 2;
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 1.5rem;
}

.header-title {
  display: flex; align-items: center; gap: 1rem;
}

.title-icon { 
  font-size: 1.8rem; 
  animation: float 3s ease-in-out infinite; 
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
}

.title-text { 
  font-size: 1.8rem; 
  font-weight: 700; 
  color: white; 
  text-shadow: 0 2px 4px rgba(0,0,0,0.2); 
}

.header-subtitle { 
  color: rgba(255,255,255,0.9); 
  font-size: 0.9rem; 
  margin: 0; 
  margin-top: 0.25rem;
}
.header-stats { 
  display: flex; 
  gap: 1rem; 
  flex-wrap: wrap; 
}

.stat-card {
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 12px; 
  padding: 1rem;
  min-width: 100px; 
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
}

.stat-card:hover { 
  transform: translateY(-4px) scale(1.02); 
  background: rgba(255,255,255,0.25); 
}

.stat-icon { 
  font-size: 1.5rem; 
  margin-bottom: 0.25rem; 
  animation: pulse 2s infinite; 
}

@keyframes pulse { 
  0%, 100% { transform: scale(1); } 
  50% { transform: scale(1.05); } 
}

.stat-value { 
  font-size: 1.5rem; 
  font-weight: 700; 
  color: white; 
}

.stat-label { 
  font-size: 0.75rem; 
  color: rgba(255,255,255,0.9); 
  text-transform: uppercase; 
  letter-spacing: 0.5px;
}
.refresh-btn {
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-size: 0.9rem;
}

.refresh-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.2);
}

.filters-section { 
  margin-bottom: 2rem; 
}

.filters-container {
  background: rgba(255,255,255,0.9);
  backdrop-filter: blur(20px);
  border-radius: 20px; 
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.filter-group { 
  display: flex; 
  gap: 1rem; 
  align-items: center; 
  flex-wrap: wrap; 
}

/* Filtres Ultra-Professionnels */
.filter-wrapper, .date-filter-wrapper {
  position: relative;
  background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 220px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.premium-filter {
  background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%);
  border: 2px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.filter-wrapper:hover, .date-filter-wrapper:hover {
  border-color: var(--primary-blue);
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(0, 27, 183, 0.15);
  background: linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%);
}

.filter-wrapper:focus-within, .date-filter-wrapper:focus-within {
  border-color: var(--secondary-blue);
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 70, 255, 0.2);
  background: linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%);
}

.filter-wrapper.has-selection, .date-filter-wrapper.has-selection {
  border-color: var(--primary-blue);
  background: linear-gradient(135deg, rgba(0,27,183,0.08) 0%, rgba(0,70,255,0.05) 100%);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.12);
}

.filter-wrapper.filter-active, .date-filter-wrapper.filter-active {
  border-color: var(--secondary-blue);
  background: linear-gradient(135deg, rgba(0,70,255,0.1) 0%, rgba(0,27,183,0.08) 100%);
  box-shadow: 0 10px 30px rgba(0, 70, 255, 0.15);
}

/* Container d'icônes premium */
.filter-icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.filter-icon {
  color: var(--primary-blue);
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
}

.filter-check-icon {
  position: absolute;
  top: -2px;
  right: -2px;
  color: #10b981;
  font-size: 0.7rem;
  background: white;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  animation: pulse 2s infinite;
  z-index: 3;
}

/* Select et input premium */
.premium-select, .premium-date, .premium-input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  cursor: pointer;
  transition: all 0.3s ease;
}

.premium-input {
  cursor: text;
  text-transform: uppercase;
}

.premium-input::placeholder {
  text-transform: none;
  color: #94a3b8;
  font-weight: 400;
}

.premium-select:focus, .premium-date:focus, .premium-input:focus {
  color: var(--primary-blue);
  font-weight: 600;
}

/* Filtre code reçu */
.code-filter {
  min-width: 200px;
}

.code-filter.active {
  border-color: var(--primary-blue);
  background: linear-gradient(135deg, rgba(0,27,183,0.08) 0%, rgba(0,70,255,0.05) 100%);
}

.clear-input-btn {
  background: rgba(239, 68, 68, 0.1);
  border: none;
  color: #ef4444;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-input-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.1);
}

.premium-select option {
  background: white;
  color: #1e293b;
  padding: 0.5rem;
  border-radius: 8px;
  font-weight: 500;
}

.premium-select option:hover {
  background: linear-gradient(135deg, rgba(0,27,183,0.1) 0%, rgba(0,70,255,0.05) 100%);
}

/* Flèche premium */
.filter-arrow {
  color: var(--primary-blue);
  font-size: 0.8rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.7;
}

.filter-arrow.rotated {
  transform: rotate(180deg);
  color: var(--secondary-blue);
  opacity: 1;
}

.filter-wrapper:hover .filter-arrow, .date-filter-wrapper:hover .filter-arrow {
  opacity: 1;
  transform: scale(1.1);
}

/* Badge premium */
.premium-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  z-index: 4;
}

/* Effet de brillance */
.filter-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  transition: left 0.6s ease;
  z-index: 1;
}

.filter-wrapper:hover .filter-shine, .date-filter-wrapper:hover .filter-shine {
  left: 100%;
}

/* Bouton Reset Ultra-Premium */
.premium-reset {
  position: relative;
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.9) 0%, rgba(75, 85, 99, 0.8) 100%);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(107, 114, 128, 0.3);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 120px;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(107, 114, 128, 0.2);
}

.premium-reset:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(107, 114, 128, 1) 0%, rgba(75, 85, 99, 0.95) 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(107, 114, 128, 0.3);
  border-color: rgba(107, 114, 128, 0.5);
}

.premium-reset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(107, 114, 128, 0.1);
}

.btn-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.6s ease;
  z-index: 1;
}

.premium-reset:hover:not(:disabled) .btn-shine {
  left: 100%;
}

/* Animations */
@keyframes pulse {
  0%, 100% { 
    transform: scale(1); 
    opacity: 1; 
  }
  50% { 
    transform: scale(1.1); 
    opacity: 0.8; 
  }
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Sélecteur de Vue Ultra-Premium */
.view-selector {
  background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(226, 232, 240, 0.6);
  border-radius: 16px;
  padding: 0.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.view-toggle-group {
  display: flex;
  gap: 0.25rem;
}

.view-toggle-btn {
  background: transparent;
  border: none;
  padding: 0.6rem 1rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.85rem;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.view-toggle-btn:hover {
  color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.05);
  transform: translateY(-1px);
}

.view-toggle-btn.active {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3);
  transform: translateY(-2px);
}

.view-toggle-btn.active:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.4);
}

/* Tableau Ultra-Professionnel */
.interventions-table-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.table-wrapper {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--primary-blue) rgba(0, 0, 0, 0.1);
}

.table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 4px;
}

.interventions-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

.table-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
}

.table-header th {
  padding: 1rem 0.75rem;
  text-align: left;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  position: sticky;
  top: 0;
  z-index: 10;
}

.table-header th i {
  margin-right: 0.5rem;
  opacity: 0.9;
}

.table-header .th-date { width: 100px; }
.table-header .th-time { width: 120px; }
.table-header .th-client { width: 180px; }
.table-header .th-contact { width: 140px; }
.table-header .th-technicien { width: 150px; }
.table-header .th-statut { width: 120px; }
.table-header .th-actions { width: 160px; text-align: center; }

.table-body {
  background: white;
}

.table-row {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.table-row:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02) 0%, rgba(0, 70, 255, 0.01) 100%);
  transform: translateX(2px);
  box-shadow: 4px 0 0 var(--primary-blue);
}

.table-row:last-child {
  border-bottom: none;
}

/* Classes de statut pour les lignes - Couleurs de fond visibles */

/* Statut: Planifié - Bleu */
.row-planned {
  background: linear-gradient(90deg, #dbeafe, #eff6ff) !important;
  border-left: 5px solid #3b82f6 !important;
}

.row-planned:hover {
  background: linear-gradient(90deg, #bfdbfe, #dbeafe) !important;
  box-shadow: 4px 0 0 #3b82f6;
  transform: scale(1.005);
}

/* Statut: En cours - Orange */
.row-progress {
  background: linear-gradient(90deg, #fef3c7, #fffbeb) !important;
  border-left: 5px solid #f59e0b !important;
}

.row-progress:hover {
  background: linear-gradient(90deg, #fde68a, #fef3c7) !important;
  box-shadow: 4px 0 0 #f59e0b;
  transform: scale(1.005);
}

/* Statut: Terminé - Vert */
.row-completed {
  background: linear-gradient(90deg, #bbf7d0, #dcfce7) !important;
  border-left: 5px solid #22c55e !important;
}

.row-completed:hover {
  background: linear-gradient(90deg, #86efac, #bbf7d0) !important;
  box-shadow: 4px 0 0 #10b981;
  transform: scale(1.005);
}

/* Statut: Reporté - Rouge */
.row-reported {
  background: linear-gradient(90deg, #fecaca, #fee2e2) !important;
  border-left: 5px solid #ef4444 !important;
}

.row-reported:hover {
  background: linear-gradient(90deg, #fca5a5, #fecaca) !important;
  box-shadow: 4px 0 0 #ef4444;
  transform: scale(1.005);
}

/* Statut: Default - Gris */
.row-default {
  background: linear-gradient(90deg, #f3f4f6, #f9fafb) !important;
  border-left: 5px solid #6b7280 !important;
}

.row-default:hover {
  background: linear-gradient(90deg, #e5e7eb, #f3f4f6) !important;
  box-shadow: 4px 0 0 #6b7280;
  transform: scale(1.005);
}

.table-row td {
  padding: 1rem 0.75rem;
  vertical-align: middle;
  font-size: 0.9rem;
}

/* Cellules spécialisées */
.date-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  min-width: 60px;
  text-align: center;
}

.date-day {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1;
}

.date-month {
  font-size: 0.7rem;
  opacity: 0.9;
  text-transform: uppercase;
}

.time-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-weight: 500;
}

.time-icon {
  color: var(--primary-blue);
  opacity: 0.7;
}

.client-cell {
  font-weight: 600;
  color: #1e293b;
}

.contact-cell .contact-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.contact-cell .contact-link:hover {
  color: var(--secondary-blue);
  transform: translateX(2px);
}

.technicien-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-weight: 500;
}

.technicien-cell i {
  color: var(--primary-blue);
  opacity: 0.7;
}

.table-badge {
  padding: 0.4rem 0.8rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  min-width: 80px;
}

/* Actions du tableau */
.table-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
}

.table-action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.table-action-btn:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.table-action-btn.start-btn {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.table-action-btn.stock-btn {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

/* Bouton Terminer Intervention Ultra-Premium - Vue Tableau */
.premium-table-affect-btn {
  position: relative;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ffb347 100%);
  border: 2px solid rgba(255, 107, 53, 0.4);
  box-shadow: 
    0 6px 20px rgba(255, 107, 53, 0.35),
    0 3px 12px rgba(247, 147, 30, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.premium-table-affect-btn:hover {
  transform: translateY(-4px) scale(1.15) rotateZ(2deg);
  box-shadow: 
    0 15px 35px rgba(255, 107, 53, 0.45),
    0 8px 20px rgba(247, 147, 30, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  border-color: rgba(255, 107, 53, 0.7);
  background: linear-gradient(135deg, #ff8c42 0%, #ff7043 50%, #ffab40 100%);
}

.premium-table-affect-btn:active {
  transform: translateY(-1px) scale(1.08);
  transition: all 0.1s ease;
}

.table-btn-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 3;
}

.table-btn-icon {
  position: relative;
  z-index: 4;
  font-size: 0.9rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25));
}

.premium-table-affect-btn:hover .table-btn-icon {
  transform: scale(1.3) rotateY(180deg);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.35));
}

.table-icon-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 28px;
  height: 28px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.7) 0%, transparent 65%);
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: all 0.3s ease;
  z-index: 2;
}

.premium-table-affect-btn:hover .table-icon-glow {
  transform: translate(-50%, -50%) scale(1);
  animation: tableIconGlow 1.8s ease-in-out infinite;
}

@keyframes tableIconGlow {
  0%, 100% { 
    opacity: 0.7; 
    transform: translate(-50%, -50%) scale(1); 
  }
  50% { 
    opacity: 1; 
    transform: translate(-50%, -50%) scale(1.4); 
  }
}

/* Effet ripple au clic */
.table-btn-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.6s ease-out;
  z-index: 1;
}

.premium-table-affect-btn:active .table-btn-ripple {
  width: 60px;
  height: 60px;
  opacity: 0;
}

/* Effet de pulsation continue */
.premium-table-affect-btn::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #ff6b35, #f7931e, #ffb347);
  border-radius: inherit;
  z-index: -1;
  opacity: 0;
  animation: tableBtnPulse 3s ease-in-out infinite;
}

@keyframes tableBtnPulse {
  0%, 100% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.05);
  }
}

.premium-table-affect-btn:hover::before {
  animation-duration: 1.5s;
  opacity: 0.5;
}

.table-action-btn.affect-btn {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.table-action-btn.view-btn {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

/* Responsive pour filtres premium */
@media (max-width: 1200px) {
  .filter-wrapper, .date-filter-wrapper {
    min-width: 180px;
    padding: 0.6rem 0.8rem;
  }
  
  .premium-reset {
    min-width: 100px;
    padding: 0.6rem 1rem;
  }

  .view-selector {
    min-width: 140px;
  }

  .view-toggle-btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }

  /* Tableau responsive */
  .interventions-table {
    min-width: 900px;
  }

  .table-header th {
    padding: 0.75rem 0.5rem;
    font-size: 0.8rem;
  }

  .table-row td {
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
  }

  .table-action-btn {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-wrapper, .date-filter-wrapper, .premium-reset, .view-selector {
    width: 100%;
    min-width: auto;
  }

  .view-toggle-group {
    justify-content: center;
  }

  /* Tableau mobile */
  .interventions-table {
    min-width: 700px;
  }

  .table-header .th-date { width: 80px; }
  .table-header .th-time { width: 100px; }
  .table-header .th-client { width: 140px; }
  .table-header .th-contact { width: 120px; }
  .table-header .th-technicien { width: 120px; }
  .table-header .th-statut { width: 100px; }
  .table-header .th-actions { width: 120px; }

  .table-header th {
    padding: 0.5rem 0.25rem;
    font-size: 0.75rem;
  }

  .table-row td {
    padding: 0.5rem 0.25rem;
    font-size: 0.8rem;
  }

  .table-action-btn {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }

  .table-actions {
    gap: 0.25rem;
  }

  /* Responsive pour boutons premium */
  .premium-affect-btn {
    min-width: 100px;
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }

  .btn-icon-wrapper {
    width: 20px;
    height: 20px;
    margin-right: 0.4rem;
  }

  .btn-icon {
    font-size: 0.9rem;
  }

  .premium-table-affect-btn {
    width: 28px;
    height: 28px;
  }

  .table-btn-icon {
    font-size: 0.8rem;
  }

  .table-icon-glow {
    width: 24px;
    height: 24px;
  }

  /* Réduction des effets sur mobile pour performance */
  .premium-affect-btn:hover {
    transform: translateY(-3px) scale(1.02);
  }

  .premium-table-affect-btn:hover {
    transform: translateY(-2px) scale(1.1);
  }

  .particle {
    display: none; /* Masquer particules sur mobile */
  }
}
.interventions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
}

.intervention-card {
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
  border-left: 5px solid var(--primary-blue);
  transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
  position: relative;
  overflow: hidden;
}

.intervention-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}

/* Statut: Planifié - Bleu */
.intervention-card.status-planned { 
  background: linear-gradient(135deg, #dbeafe, #eff6ff) !important;
  border-left: 5px solid #3b82f6 !important;
  border: 2px solid #3b82f6;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
}

.intervention-card.status-planned:hover {
  background: linear-gradient(135deg, #bfdbfe, #dbeafe) !important;
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.25);
}

/* Statut: En cours - Orange */
.intervention-card.status-progress { 
  background: linear-gradient(135deg, #fef3c7, #fffbeb) !important;
  border-left: 5px solid #f59e0b !important;
  border: 2px solid #f59e0b;
  box-shadow: 0 8px 25px rgba(245, 158, 11, 0.15);
}

.intervention-card.status-progress:hover {
  background: linear-gradient(135deg, #fde68a, #fef3c7) !important;
  box-shadow: 0 15px 40px rgba(245, 158, 11, 0.25);
}

/* Statut: Terminé - Vert */
.intervention-card.status-completed { 
  background: linear-gradient(135deg, #bbf7d0, #dcfce7) !important;
  border-left: 5px solid #22c55e !important;
  border: 2px solid #22c55e;
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.15);
}

.intervention-card.status-completed:hover {
  background: linear-gradient(135deg, #86efac, #bbf7d0) !important;
  box-shadow: 0 15px 40px rgba(34, 197, 94, 0.25);
}

/* Statut: Reporté - Rouge */
.intervention-card.status-reported { 
  background: linear-gradient(135deg, #fecaca, #fee2e2) !important;
  border-left: 5px solid #ef4444 !important;
  border: 2px solid #ef4444;
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.15);
}

.intervention-card.status-reported:hover {
  background: linear-gradient(135deg, #fca5a5, #fecaca) !important;
  box-shadow: 0 15px 40px rgba(239, 68, 68, 0.25);
}

/* Statut: Default - Gris */
.intervention-card.status-default { 
  background: linear-gradient(135deg, #f3f4f6, #f9fafb) !important;
  border-left: 5px solid #6b7280 !important;
  border: 2px solid #6b7280;
  box-shadow: 0 8px 25px rgba(107, 114, 128, 0.15);
}

.intervention-card.status-default:hover {
  background: linear-gradient(135deg, #e5e7eb, #f3f4f6) !important;
  box-shadow: 0 15px 40px rgba(107, 114, 128, 0.25);
}
.card-header {
  display: flex; 
  justify-content: space-between; 
  align-items: center;
  margin-bottom: 1rem; 
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.card-date {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white; 
  padding: 0.5rem 1rem; 
  border-radius: 12px;
  text-align: center; 
  min-width: 60px;
}

.date-day { font-size: 1.2rem; font-weight: 700; }
.date-month { font-size: 0.8rem; opacity: 0.9; }

.card-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-weight: 500;
}

.status-badge {
  padding: 0.5rem 1rem; 
  border-radius: 20px;
  font-size: 0.8rem; 
  font-weight: 600;
  text-transform: uppercase; 
  letter-spacing: 0.5px;
}

.badge-planned { background: linear-gradient(135deg, #3b82f6, #1d4ed8); color: white; }
.badge-progress { background: linear-gradient(135deg, #f59e0b, #d97706); color: white; }
.badge-completed { background: linear-gradient(135deg, #10b981, #059669); color: white; }
.badge-reported { background: linear-gradient(135deg, #ef4444, #dc2626); color: white; }

.card-body {
  margin-bottom: 1.5rem;
}

/* Type de mission et Prix dans les cartes */
.card-mission-info {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.mission-type {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
}

.mission-price {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* Styles pour le tableau */
.type-badge-table {
  display: inline-block;
  padding: 0.35rem 0.7rem;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.prix-badge-table {
  display: inline-block;
  padding: 0.35rem 0.7rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.td-type, .td-prix {
  white-space: nowrap;
}

.type-cell, .prix-cell {
  display: flex;
  align-items: center;
}

.client-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.client-contact, .card-technicien {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.card-actions {
  display: flex; 
  gap: 0.75rem; 
  flex-wrap: wrap;
  margin-top: 1.5rem; 
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.action-btn {
  padding: 0.75rem 1.25rem; 
  border: none; 
  border-radius: 12px;
  font-weight: 600; 
  cursor: pointer;
  display: flex; 
  align-items: center; 
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
  flex: 1; 
  min-width: 120px; 
  justify-content: center;
}

.start-btn { background: linear-gradient(135deg, #10b981, #059669); color: white; }
.stock-btn { background: linear-gradient(135deg, #8b5cf6, #7c3aed); color: white; }
/* Bouton Terminer Intervention Ultra-Premium - Vue Cartes */
.premium-affect-btn {
  position: relative;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ffb347 100%);
  border: 2px solid rgba(255, 107, 53, 0.3);
  box-shadow: 
    0 8px 25px rgba(255, 107, 53, 0.3),
    0 4px 15px rgba(247, 147, 30, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  overflow: hidden;
  transform-style: preserve-3d;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.premium-affect-btn:hover {
  transform: translateY(-6px) scale(1.05) rotateX(5deg);
  box-shadow: 
    0 20px 40px rgba(255, 107, 53, 0.4),
    0 10px 25px rgba(247, 147, 30, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  border-color: rgba(255, 107, 53, 0.6);
  background: linear-gradient(135deg, #ff8c42 0%, #ff7043 50%, #ffab40 100%);
}

.premium-affect-btn:active {
  transform: translateY(-2px) scale(1.02);
  transition: all 0.1s ease;
}

.btn-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 0.5rem;
}

.btn-icon {
  position: relative;
  z-index: 3;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.premium-affect-btn:hover .btn-icon {
  transform: scale(1.2) rotateY(360deg);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.icon-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 30px;
  height: 30px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: all 0.3s ease;
  z-index: 2;
}

.premium-affect-btn:hover .icon-glow {
  transform: translate(-50%, -50%) scale(1);
  animation: iconGlow 2s ease-in-out infinite;
}

@keyframes iconGlow {
  0%, 100% { 
    opacity: 0.6; 
    transform: translate(-50%, -50%) scale(1); 
  }
  50% { 
    opacity: 0.9; 
    transform: translate(-50%, -50%) scale(1.3); 
  }
}

.btn-text {
  position: relative;
  z-index: 3;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.premium-affect-btn:hover .btn-text {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transform: translateX(2px);
}

/* Particules animées */
.btn-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  opacity: 0;
  animation: particleFloat 3s ease-in-out infinite;
}

.particle:nth-child(1) {
  top: 20%;
  left: 20%;
  animation-delay: 0s;
}

.particle:nth-child(2) {
  top: 60%;
  left: 70%;
  animation-delay: 1s;
}

.particle:nth-child(3) {
  top: 80%;
  left: 30%;
  animation-delay: 2s;
}

@keyframes particleFloat {
  0%, 100% {
    opacity: 0;
    transform: translateY(0px) scale(0.5);
  }
  50% {
    opacity: 1;
    transform: translateY(-20px) scale(1);
  }
}

.premium-affect-btn:hover .particle {
  animation-duration: 1.5s;
}

/* Effet de brillance avancé */
.btn-shine-effect {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 30%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0.4) 70%,
    transparent 100%
  );
  transition: left 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  transform: skewX(-20deg);
}

.premium-affect-btn:hover .btn-shine-effect {
  left: 100%;
}

.affect-btn { background: linear-gradient(135deg, #f59e0b, #d97706); color: white; }
.report-btn { background: linear-gradient(135deg, #ef4444, #dc2626); color: white; }
.view-btn { background: linear-gradient(135deg, #6b7280, #4b5563); color: white; }
.delete-btn { background: linear-gradient(135deg, #dc2626, #b91c1c); color: white; }

.action-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}

.delete-btn:hover {
  background: linear-gradient(135deg, #b91c1c, #991b1b);
  box-shadow: 0 10px 30px rgba(220, 38, 38, 0.4);
}

/* Style pour le bouton supprimer dans le tableau */
.table-action-btn.delete-btn {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.table-action-btn.delete-btn:hover {
  background: linear-gradient(135deg, #b91c1c, #991b1b);
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.4);
}

/* Modal de confirmation de suppression */
.delete-confirm-modal {
  max-width: 500px;
  width: 90%;
}

.delete-header {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.delete-header h4 {
  color: white;
}

.delete-header .close-btn {
  color: white;
}

.delete-warning {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.warning-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #fecaca, #fee2e2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.warning-icon i {
  font-size: 1.5rem;
  color: #dc2626;
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.intervention-info {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

.intervention-info p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
  color: #4b5563;
}

.warning-text {
  color: #dc2626;
  font-size: 0.9rem;
  font-weight: 500;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

/* Bouton Reporter Admin */
.report-btn-admin {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
  transition: all 0.3s ease;
}

.report-btn-admin:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
}

/* Modal Admin Report */
.admin-report-modal {
  max-width: 800px;
  width: 90%;
}

.intervention-summary {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  border-left: 4px solid #3b82f6;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.intervention-summary h5 {
  margin: 0 0 0.25rem 0;
  color: #1e40af;
  font-size: 1.1rem;
  font-weight: 700;
}

.intervention-summary p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.current-info-card {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border-left: 4px solid #2196f3;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.current-info-card h6 {
  margin: 0 0 1rem 0;
  color: #1976d2;
  font-size: 1.1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item .label {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.info-item .value {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.95rem;
}

.date-modification-section {
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  border-left: 4px solid #ff9800;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.date-modification-section h6 {
  margin: 0 0 1rem 0;
  color: #f57c00;
  font-size: 1.1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-inputs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.date-inputs-grid .form-group {
  margin-top: 0;
}

.date-inputs-grid label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #424242;
  margin-bottom: 0.5rem;
  display: block;
}

.date-inputs-grid .form-control {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.date-inputs-grid .form-control:focus {
  outline: none;
  border-color: #ff9800;
  box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.1);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.modal-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-footer {
  padding: 1.25rem 1.5rem;
  background: #f9fafb;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.modal-footer .btn {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background: #4b5563;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Bouton Check-list */
.checklist-btn {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.checklist-btn:hover {
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

/* Modal Check-list */
.checklist-modal {
  max-width: 500px;
  width: 90%;
}

.checklist-section {
  margin-bottom: 1.5rem;
}

.checklist-section h5 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e2e8f0;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.checklist-item:hover {
  background: #f1f5f9;
}

.checklist-item input[type="checkbox"] {
  width: 20px;
  height: 20px;
  accent-color: #10b981;
  cursor: pointer;
}

.checklist-item label {
  flex: 1;
  cursor: pointer;
  font-size: 0.95rem;
  color: #334155;
}

.checklist-item input[type="checkbox"]:checked + label {
  color: #10b981;
  text-decoration: line-through;
}

.checklist-progress {
  background: #f1f5f9;
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1rem;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #64748b;
}

.progress-percent {
  font-weight: 600;
  color: #10b981;
}

.checklist-progress .progress-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.checklist-progress .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* Section Photos */
.photos-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid #e2e8f0;
}

.photos-section h5 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.photo-upload-container {
  margin-bottom: 1.5rem;
}

.upload-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.upload-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.upload-icon.before {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
}

.upload-icon.after {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.upload-info h6 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.upload-info p {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
}

.upload-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
}

.upload-zone:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-zone.drag-active {
  border-color: #10b981;
  background: #ecfdf5;
}

.hidden-input {
  display: none;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon-large {
  font-size: 2.5rem;
  color: #94a3b8;
}

.upload-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #475569;
}

.upload-hint {
  font-size: 0.75rem;
  color: #94a3b8;
}

.upload-btn {
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.photo-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.75rem;
}

.photo-preview-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
}

.photo-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-photo-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  transition: all 0.2s ease;
}

.remove-photo-btn:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.add-more-photo {
  aspect-ratio: 1;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  font-size: 1.5rem;
  transition: all 0.2s ease;
}

.add-more-photo:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #eff6ff;
}

/* Agrandir le modal pour les photos */
.checklist-modal {
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

/* ========== RESPONSIVE MOBILE ========== */
@media (max-width: 768px) {
  .interventions-manager {
    padding: 0.5rem;
  }
  
  /* Header responsive */
  .interventions-header {
    padding: 1rem;
    border-radius: 12px;
    margin-bottom: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-info {
    text-align: center;
  }
  
  .title-text {
    font-size: 1.4rem;
  }
  
  .header-subtitle {
    font-size: 0.8rem;
  }
  
  .header-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    width: 100%;
  }
  
  .stat-card {
    padding: 0.75rem 0.5rem;
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .stat-icon {
    font-size: 1.25rem;
  }
  
  .stat-value {
    font-size: 1.25rem;
  }
  
  .stat-label {
    font-size: 0.7rem;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .refresh-btn {
    width: 100%;
    justify-content: center;
  }
  
  /* Filtres responsive */
  .filters-section {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
  
  .filters-container {
    padding: 0;
  }
  
  .filter-group {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
  
  .filter-wrapper,
  .date-filter-wrapper {
    width: 100%;
    min-width: auto;
  }
  
  /* Cacher le toggle de vue sur mobile - forcer les cartes */
  .view-selector {
    display: none !important;
  }
  
  .reset-filters-btn {
    width: 100%;
    justify-content: center;
  }
  
  /* Cacher le tableau sur mobile */
  .interventions-table-container {
    display: none !important;
  }
  
  /* Grille des cartes responsive */
  .interventions-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0;
  }
  
  .intervention-card {
    border-radius: 12px;
  }
  
  .card-header {
    padding: 0.875rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .card-date {
    min-width: 50px;
  }
  
  .date-day {
    font-size: 1.25rem;
  }
  
  .date-month {
    font-size: 0.7rem;
  }
  
  .card-time {
    font-size: 0.85rem;
  }
  
  .card-body {
    padding: 0.875rem;
  }
  
  .client-name {
    font-size: 1rem;
  }
  
  .client-contact {
    font-size: 0.85rem;
  }
  
  .card-technicien {
    font-size: 0.85rem;
  }
  
  .card-actions {
    padding: 0.875rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .action-btn {
    flex: 1;
    min-width: calc(50% - 0.25rem);
    padding: 0.625rem 0.5rem;
    font-size: 0.8rem;
  }
  
  .action-btn span {
    display: inline;
  }
  
  /* Modals responsive */
  .modal-content {
    width: 95%;
    max-width: none;
    margin: 1rem;
    max-height: 90vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-header h4 {
    font-size: 1.1rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .modal-footer .btn {
    width: 100%;
  }
  
  .date-inputs-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .interventions-manager {
    padding: 0.25rem;
  }
  
  .interventions-header {
    padding: 0.75rem;
  }
  
  .title-text {
    font-size: 1.2rem;
  }
  
  .header-stats {
    gap: 0.25rem;
  }
  
  .stat-card {
    padding: 0.5rem 0.25rem;
  }
  
  .stat-value {
    font-size: 1.1rem;
  }
  
  .stat-label {
    font-size: 0.65rem;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .card-status {
    width: 100%;
  }
  
  .status-badge {
    width: 100%;
    text-align: center;
  }
  
  .card-actions {
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
    min-width: auto;
  }
}

/* Pagination Styles */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: white;
  border-radius: 12px;
  margin-top: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-info {
  color: #64748b;
  font-size: 0.9rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.items-per-page label {
  color: #64748b;
  font-size: 0.85rem;
}

.items-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #1e293b;
  font-size: 0.85rem;
  cursor: pointer;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: var(--primary-blue);
  color: var(--primary-blue);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.page-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover {
  background: #f1f5f9;
  border-color: var(--primary-blue);
  color: var(--primary-blue);
}

.page-btn.active {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-color: var(--primary-blue);
  color: white;
}

@media (max-width: 768px) {
  .pagination-section {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
  
  .pagination-controls {
    justify-content: center;
  }
  
  .pagination-buttons {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .pagination-btn {
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }
  
  .page-btn {
    width: 32px;
    height: 32px;
  }
}
</style>
